/**
 * AppContent is the authenticated shell. Responsibilities:
 * - Hydrate session/client state via custom hooks (`useSessionSync`,
 *   `useClientData`, `useViewportFlags`). Role/profile hydration runs in
 *   `AuthGate` (`useInitUserRole`) before AppContent mounts.
 * - Manage global UI state (sidebar, focus mode, view-settings sheet, debug toggles).
 * - Render the appropriate route graph (member vs. internal) using the helper
 *   renderers from `routes/MemberRoutes` and `routes/InternalRoutes`.
 * - Mount global chrome (command palettes, chat, floating controls, view settings).
 */
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  PAGE_RECORDING_CONTEXT_TYPES,
  SUPPORTED_PAGE_RECORDING_CONTEXT_TYPES,
} from '@/apis/page-recordings';
import { clearAllThriveIntroStorage } from '@/components/member/thrive-check/thriveIntroStorage';
import { FEATURE_FLAGS, isFeatureEnabled, isFeatureStrictlyEnabled } from '@/config/feature-flags';
import { getNavigationItemPermission } from '@/config/navigation-permission-registry';
import {
  SidebarMode,
  SIDEBAR_WIDTHS,
  getAvailableSidebarModes,
  getDefaultSidebarMode,
  getNextSidebarMode,
  migrateSidebarState,
} from '@/config/sidebar-config';
import { CORPORATE_CONTENT_TOP_OFFSET } from '@/design-system/constants/spacing';
import { signOutSession } from '@/lib/sign-out';
import { LEADS_ALLOWED_ROLES } from '@/pages/leads/leadConstants';
import { clearPlatformChatStorageForUser } from '@/utils/platformChat';
import PermissionBasedRoute from '../components/auth/PermissionBasedRoute';
import { CorporateHeader } from '../components/corporate/CorporateHeader';
import FallbackSpinner from '../components/FallbackSpinner';
import { GoogleCalendarModal } from '../components/GoogleCalendarModal';
import { MemberSidebar } from '../components/member/MemberSidebar';
import NetworkErrorPage from '../components/NetworkErrorPage';
import RoleBasedRoute from '../components/RoleBasedRoute';
import { UniversalCommandPalette } from '../components/search/UniversalCommandPalette';
import { InternalSidebar } from '../components/superclinic/InternalSidebar';
import Unauthorized from '../components/superclinic/Unauthorized';
import { useFocusMode } from '../contexts/FocusModeContext';
import { useViewSettings } from '../contexts/ViewSettingsContext';
import { useEffectiveHomePath } from '../hooks/useEffectiveHomePath';
import { useMemberClient } from '../hooks/useMemberClient';
import { useRecordingContext, type RecordingContext } from '../hooks/useRecordingContext';
import Header from '../layout/Header';
import { NotFoundPage } from '../pages/NotFoundPage';
import { useFeatureFlagsContext } from '../providers/FeatureFlagsProvider';
import { RootState } from '../redux/store';
import { GlobalChrome } from './components/GlobalChrome';
import { MobileAppNavigation } from './components/MobileAppNavigation';
import { ViewSettingsSheet } from './components/ViewSettingsSheet';
import { useClientData } from './hooks/useClientData';
import { useSessionSync } from './hooks/useSessionSync';
import { useViewportFlags } from './hooks/useViewportFlags';
import { renderCorporateAdminRoutes, renderInternalRoutes } from './routes/InternalRoutes';
import {
  AlgorithmsTourPage,
  ChatDemoPage,
  LeadsPage,
  LeadEditorPage,
  PageRecordingsPage,
  PlatformChatPage,
  ReportsCompare,
  ThriveReport,
  UiPreferencesPage,
} from './routes/lazyPages';
import { renderMemberRoutes } from './routes/MemberRoutes';

export const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userRole = useSelector<RootState, string | null>((state) => state.user.role);
  const username = useSelector<RootState, string | null>((state) => state.user.username);
  const userId = useSelector<RootState, string | null>((state) => state.user.user_id);
  const {
    viewSettings,
    updateViewSetting,
    updateClientDetailsTabSetting,
    updateClientDiscoveryTabSetting,
    updateCorporateDashboardTabSetting,
    resetViewSettings,
  } = useViewSettings();
  const { flags, isLoading: areFeatureFlagsLoading } = useFeatureFlagsContext();
  const inAppChatWebappEnabled = isFeatureStrictlyEnabled(
    flags,
    FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP
  );
  const showClientTickets = isFeatureEnabled(flags, FEATURE_FLAGS.ENABLE_CLIENT_TICKETS);
  const isCorporateAdminUsersView =
    location.pathname.startsWith('/users') &&
    new URLSearchParams(location.search).get('view') === 'corporates';
  const shouldHideFloatingSupportActions =
    userRole === 'CORPORATE_ADMIN' ||
    location.pathname.startsWith('/corporate') ||
    isCorporateAdminUsersView;
  const { isFocusMode } = useFocusMode();
  const { isDesktop } = useViewportFlags();
  const hasMobileNavigation =
    !isDesktop &&
    !isFocusMode &&
    !!userRole &&
    userRole !== 'CLIENT' &&
    userRole !== 'CORPORATE_ADMIN' &&
    !location.pathname.startsWith('/member/');
  useSessionSync();

  const { selectedClient, isClientLoading, networkError, fetchClientData } = useClientData({
    username,
    userRole,
  });
  const { setRecordingContext } = useRecordingContext();

  const isStaffOnMemberRoute =
    userRole !== null &&
    userRole !== 'CLIENT' &&
    userRole !== 'CORPORATE_ADMIN' &&
    location.pathname.startsWith('/member/');
  const memberRouteClientId =
    location.pathname.match(/\/member\/[^/]+\/([0-9a-f-]{36})/)?.[1] || '';
  const { client: memberViewClient } = useMemberClient(null, null, memberRouteClientId);

  // Resolve the user's saved homepage (if any) so post-login redirects can
  // honor it across roles. CLIENT uses the active client id for tokenized
  // paths like /member/home/:clientId; staff paths usually have no tokens.
  const effectiveClientId = selectedClient?.id ?? memberRouteClientId ?? '';
  const { effectiveHomePath, isLoading: isLoadingUiPrefs } =
    useEffectiveHomePath(effectiveClientId);

  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(() => {
    const available = getAvailableSidebarModes();
    const migrated = migrateSidebarState();
    if (migrated && available.includes(migrated)) return migrated;

    const stored = localStorage.getItem('sidebarMode') as SidebarMode | null;
    if (stored && available.includes(stored)) return stored;

    if (location.pathname.includes('/roadmap/compare/')) {
      return available.includes('icon-only') ? 'icon-only' : available[available.length - 1];
    }
    return getDefaultSidebarMode();
  });
  const [isSidebarHidden, setIsSidebarHidden] = useState(
    () => localStorage.getItem('sidebarHidden') === 'true'
  );
  const showSidebar = !isDesktop || (!isFocusMode && !isSidebarHidden);
  const sidebarOffset =
    showSidebar && isDesktop && userRole !== 'CORPORATE_ADMIN'
      ? SIDEBAR_WIDTHS[sidebarMode].offset
      : '0px';
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showViewSettings, setShowViewSettings] = useState(false);

  useEffect(() => {
    if (isDesktop) setIsMobileSidebarOpen(false);
  }, [isDesktop]);
  const [apiError, setApiError] = useState<{
    message: string;
    isAuthError?: boolean;
  } | null>(null);

  const handleLogout = useCallback(async () => {
    try {
      clearAllThriveIntroStorage();
      clearPlatformChatStorageForUser(userId);
      await signOutSession({ dispatch, queryClient });
      navigate('/member/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [dispatch, navigate, queryClient, userId]);

  useEffect(() => {
    if (apiError) {
      if (apiError.isAuthError) {
        toast.error(apiError.message, { id: 'auth-error-toast' });
        handleLogout();
      } else {
        toast.error(apiError.message);
      }
      setApiError(null);
    }
  }, [apiError, handleLogout]);

  useEffect(() => {
    if (location.pathname.includes('/roadmap/compare/')) {
      const available = getAvailableSidebarModes();
      setSidebarMode(
        available.includes('icon-only') ? 'icon-only' : available[available.length - 1]
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebarMode', sidebarMode);
  }, [sidebarMode]);

  useEffect(() => {
    localStorage.setItem('sidebarHidden', String(isSidebarHidden));
  }, [isSidebarHidden]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        updateViewSetting('debugPanel', !viewSettings.debugPanel);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarMode((prev) => getNextSidebarMode(prev));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewSettings.debugPanel, updateViewSetting]);

  useEffect(() => {
    const handleOpenViewSettings = () => {
      setShowViewSettings(true);
    };
    window.addEventListener('openViewSettings', handleOpenViewSettings);
    return () => window.removeEventListener('openViewSettings', handleOpenViewSettings);
  }, []);

  useEffect(() => {
    // Wait for UI prefs to load before redirecting to avoid flashing the
    // role default when the user has a custom homepage saved.
    if (isLoadingUiPrefs) return;
    if (location.pathname !== '/' || !userRole) return;

    switch (userRole) {
      case 'CLIENT':
        // Handled by MemberRoutes once `selectedClient` resolves.
        break;
      case 'CORPORATE_ADMIN':
        navigate('/corporate', { replace: true });
        break;
      case 'CLINICAL_SUPPORT':
      case 'NUTRITIONIST':
      case 'PHARMACIST':
      case 'SUPER_DOCTOR':
      case 'OPERATIONS_SUPPORT':
      case 'DOCTOR':
      case 'SUPER_ADMIN':
      case 'ADMIN':
      case 'BUSINESS_DEVELOPMENT':
      case 'READ_ONLY':
        navigate(effectiveHomePath ?? '/hub', { replace: true });
        break;
      default:
        break;
    }
  }, [userRole, location.pathname, navigate, effectiveHomePath, isLoadingUiPrefs]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const detailsTab = queryParams.get('tab')?.toLowerCase() ?? '';
    const normalizedDetailsTab = detailsTab.replace(/[^a-z0-9_-]/g, '');
    const isClientDetailsRoute = /\/client\/[0-9a-f-]{36}\/details/.test(location.pathname);

    const pathToPageKey = (pathname: string) => {
      if (pathname.startsWith('/chat') || pathname.startsWith('/member/chat')) return 'chat';
      if (pathname.startsWith('/settings') || pathname.startsWith('/member/settings'))
        return 'settings';
      if (pathname.startsWith('/nutrition/intake')) return 'nutrition.intake';
      if (pathname.startsWith('/appointments')) return 'appointments.detail';
      if (isClientDetailsRoute && normalizedDetailsTab) {
        return `client.${normalizedDetailsTab}`;
      }
      if (pathname.startsWith('/member/profile') || pathname.includes('/profile'))
        return 'client.profile';
      if (isClientDetailsRoute) return 'client.details';
      if (pathname.startsWith('/member/reports') || pathname.includes('/report'))
        return 'reports.detail';
      if (
        pathname === '/hub' ||
        pathname === '/member/home' ||
        pathname.startsWith('/member/home/')
      )
        return 'dashboard.home';
      return (
        pathname.replace(/\//g, '.').replace(/\.+/g, '.').replace(/^\./, '') || 'dashboard.home'
      );
    };
    const routeClientProfileId =
      location.pathname.match(/\/client\/([0-9a-f-]{36})\/details/)?.[1] ??
      location.pathname.match(/\/member\/profile\/([0-9a-f-]{36})/)?.[1] ??
      undefined;
    const pageKey = pathToPageKey(location.pathname);

    const baseContext: RecordingContext = {
      page_key: pageKey,
      client: routeClientProfileId ?? selectedClient?.id ?? memberViewClient?.id ?? undefined,
      title: 'Voice note',
      metadata: {
        route: location.pathname,
      },
    };

    if (pageKey === 'client.profile') {
      const clientProfileId =
        routeClientProfileId ?? selectedClient?.id ?? memberViewClient?.id ?? undefined;
      baseContext.client = clientProfileId;
      baseContext.context_type = PAGE_RECORDING_CONTEXT_TYPES.userProfile;
      baseContext.context_id = clientProfileId;
    }

    if (isClientDetailsRoute) {
      const clientProfileId =
        routeClientProfileId ?? selectedClient?.id ?? memberViewClient?.id ?? undefined;
      // The URL `tab` slug (e.g. "voice-recordings", "clinical-notes") is
      // free-form and does not map 1:1 to the backend's recording context
      // enum. Fall back to a generic `client` context whenever the tab is
      // missing or not in the supported set; the original tab slug is kept
      // in `metadata.tab` so downstream consumers can still see which tab
      // the recording originated from.
      const resolvedContextType = SUPPORTED_PAGE_RECORDING_CONTEXT_TYPES.has(normalizedDetailsTab)
        ? normalizedDetailsTab
        : PAGE_RECORDING_CONTEXT_TYPES.client;
      baseContext.client = clientProfileId;
      baseContext.context_type = resolvedContextType;
      baseContext.context_id = clientProfileId;
      baseContext.title =
        normalizedDetailsTab && normalizedDetailsTab !== 'client_details'
          ? `${normalizedDetailsTab.replace(/_/g, ' ')} voice note`
          : 'Client details voice note';
      baseContext.metadata = {
        ...baseContext.metadata,
        tab: normalizedDetailsTab || 'client_details',
      };
    }

    setRecordingContext(baseContext);
  }, [
    location.pathname,
    location.search,
    memberViewClient?.id,
    selectedClient?.id,
    setRecordingContext,
  ]);

  const renderRoutes = () => {
    if (import.meta.env.DEV) {
      // Dev-only route to preview the loading spinner
      if (location.pathname === '/loading-preview') {
        return <Route path="/loading-preview" element={<FallbackSpinner />} />;
      }
    }

    if (!userRole) {
      return <Route path="*" element={<FallbackSpinner />} />;
    }

    if (userRole === 'CLIENT') {
      if (isClientLoading) {
        return <Route path="*" element={<FallbackSpinner />} />;
      }

      if (networkError) {
        return (
          <Route
            path="*"
            element={
              <NetworkErrorPage
                error={networkError.message}
                isAuthError={networkError.isAuthError}
                onRetry={fetchClientData}
              />
            }
          />
        );
      }

      if (!selectedClient) {
        return <Route path="*" element={<Navigate to="/member/login" replace />} />;
      }
    }

    const memberHomeForClient = selectedClient
      ? (effectiveHomePath ?? `/member/home/${selectedClient.id}`)
      : null;

    return (
      <>
        {userRole !== 'CLIENT' && userRole !== 'CORPORATE_ADMIN' && (
          <Route path="/member/login" element={<Navigate to="/hub" replace />} />
        )}
        {userRole === 'CORPORATE_ADMIN' && (
          <>
            <Route path="/" element={<Navigate to="/corporate" replace />} />
            <Route path="/member/login" element={<Navigate to="/corporate" replace />} />
          </>
        )}
        {userRole === 'CLIENT' && selectedClient && memberHomeForClient && (
          <>
            <Route path="/member/login" element={<Navigate to={memberHomeForClient} replace />} />
            <Route path="/hub" element={<Navigate to={memberHomeForClient} replace />} />
          </>
        )}
        {userRole === 'CLIENT'
          ? selectedClient &&
            renderMemberRoutes({
              selectedClient,
              isMobileSidebarOpen,
              onMobileSidebarToggle: () => setIsMobileSidebarOpen((prev) => !prev),
              sidebarMode,
              onSidebarToggle: () => setSidebarMode((prev) => getNextSidebarMode(prev)),
              effectiveHomePath,
              inAppChatWebappEnabled,
              areFeatureFlagsLoading,
            })
          : userRole === 'CORPORATE_ADMIN'
            ? renderCorporateAdminRoutes()
            : renderInternalRoutes({
                selectedClient,
                onMobileSidebarToggle: () => setIsMobileSidebarOpen((prev) => !prev),
                effectiveHomePath,
                isLoadingUiPrefs,
                showClientTickets,
              })}
        {userRole !== 'CLIENT' && userRole !== 'CORPORATE_ADMIN' && (
          <Route path="/leads" element={<RoleBasedRoute allowedRoles={LEADS_ALLOWED_ROLES} />}>
            <Route
              index
              element={
                <Suspense fallback={<FallbackSpinner />}>
                  <LeadsPage />
                </Suspense>
              }
            />
            <Route
              path="new"
              element={
                <Suspense fallback={<FallbackSpinner />}>
                  <LeadEditorPage />
                </Suspense>
              }
            />
            <Route
              path=":leadId"
              element={
                <Suspense fallback={<FallbackSpinner />}>
                  <LeadEditorPage />
                </Suspense>
              }
            />
          </Route>
        )}
        {userRole !== 'CLIENT' && userRole !== 'CORPORATE_ADMIN' && (
          <Route
            path="/page-recordings"
            element={
              <PermissionBasedRoute
                requiredPermission={getNavigationItemPermission('page-recordings')}
              />
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<FallbackSpinner />}>
                  <PageRecordingsPage />
                </Suspense>
              }
            />
          </Route>
        )}
        <Route path="/unauthorized" element={<Unauthorized />} />
        {userRole !== 'CORPORATE_ADMIN' && (
          <>
            <Route path="/algorithms-tour" element={<AlgorithmsTourPage />} />
            <Route path="/chat-demo" element={<ChatDemoPage />} />
          </>
        )}
        <Route
          path="/platform-chat"
          element={
            <PermissionBasedRoute
              requiredPermission={getNavigationItemPermission('platform-chat')}
            />
          }
        >
          <Route index element={<PlatformChatPage />} />
        </Route>
        <Route
          path="/preferences"
          element={
            <PermissionBasedRoute
              requiredPermission={getNavigationItemPermission('shared-preferences')}
            />
          }
        >
          <Route index element={<UiPreferencesPage />} />
        </Route>
        <Route
          path="/thrive-report/:clientId"
          element={
            <PermissionBasedRoute
              requiredPermission={{ feature: 'internal_reports', action: 'read' }}
            />
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<FallbackSpinner />}>
                <ThriveReport />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/reports-compare"
          element={
            <PermissionBasedRoute
              requiredPermission={{ feature: 'external_reports', action: 'list' }}
            />
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<FallbackSpinner />}>
                <ReportsCompare />
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </>
    );
  };

  return (
    <div
      className={`h-[100dvh] text-foreground antialiased overflow-hidden ${userRole === 'CLIENT' || isStaffOnMemberRoute ? 'app-background' : 'internal-app-background'}`}
    >
      <div
        className="relative h-full"
        style={
          {
            '--app-toolbar-height': userRole
              ? isDesktop
                ? '40px'
                : 'calc(48px + env(safe-area-inset-top))'
              : '0px',
            '--app-content-height':
              userRole === 'CORPORATE_ADMIN'
                ? `calc(100dvh - ${CORPORATE_CONTENT_TOP_OFFSET}px - var(--app-toolbar-height))`
                : hasMobileNavigation
                  ? 'calc(100dvh - var(--app-toolbar-height) - 64px - env(safe-area-inset-bottom))'
                  : 'calc(100dvh - var(--app-toolbar-height))',
          } as CSSProperties
        }
      >
        {userRole === 'CORPORATE_ADMIN' ? (
          <CorporateHeader />
        ) : showSidebar && userRole === 'CLIENT' && selectedClient ? (
          <MemberSidebar
            sidebarMode={sidebarMode}
            onToggle={() => setSidebarMode((prev) => getNextSidebarMode(prev))}
            client={selectedClient}
            isMobileOpen={isMobileSidebarOpen}
            onMobileToggle={() => setIsMobileSidebarOpen((prev) => !prev)}
          />
        ) : showSidebar && isStaffOnMemberRoute && memberViewClient ? (
          <MemberSidebar
            sidebarMode={sidebarMode}
            onToggle={() => setSidebarMode((prev) => getNextSidebarMode(prev))}
            client={memberViewClient}
            isMobileOpen={isMobileSidebarOpen}
            onMobileToggle={() => setIsMobileSidebarOpen((prev) => !prev)}
          />
        ) : showSidebar && userRole && userRole !== 'CLIENT' && userRole !== 'CORPORATE_ADMIN' ? (
          <InternalSidebar
            sidebarMode={sidebarMode}
            onToggle={() => setSidebarMode((prev) => getNextSidebarMode(prev))}
            isMobileOpen={isMobileSidebarOpen}
            onMobileToggle={() => setIsMobileSidebarOpen((prev) => !prev)}
          />
        ) : null}
        {userRole && (
          <Header
            contentLeft={Number.parseInt(sidebarOffset, 10)}
            isMobile={!isDesktop}
            sidebarOpen={isDesktop ? showSidebar : isMobileSidebarOpen}
            onSidebarToggle={() =>
              isDesktop
                ? setIsSidebarHidden((hidden) => !hidden)
                : setIsMobileSidebarOpen((open) => !open)
            }
            showSupportActions={!shouldHideFloatingSupportActions}
          />
        )}
        {userRole !== 'CLIENT' && (
          <Suspense fallback={null}>
            <UniversalCommandPalette />
            <GoogleCalendarModal />
          </Suspense>
        )}
        {hasMobileNavigation && userRole && (
          <MobileAppNavigation
            userRole={userRole}
            menuOpen={isMobileSidebarOpen}
            onMenuToggle={() => setIsMobileSidebarOpen((prev) => !prev)}
            showChat={!shouldHideFloatingSupportActions}
          />
        )}
        <main
          className={
            location.pathname.startsWith('/member/marketplace/') ||
            location.pathname === '/algorithms-tour'
              ? 'overflow-y-auto overflow-x-hidden'
              : 'overflow-hidden'
          }
          style={{
            position: 'absolute',
            top:
              userRole === 'CORPORATE_ADMIN'
                ? `calc(${CORPORATE_CONTENT_TOP_OFFSET}px + var(--app-toolbar-height))`
                : 'var(--app-toolbar-height)',
            left: sidebarOffset,
            right: '0px',
            bottom: hasMobileNavigation ? 'calc(64px + env(safe-area-inset-bottom))' : '0px',
          }}
        >
          {location.pathname.startsWith('/member/marketplace/') ||
          location.pathname === '/algorithms-tour' ? (
            <Suspense fallback={<FallbackSpinner />}>
              <Routes>{renderRoutes()}</Routes>
            </Suspense>
          ) : (
            <div
              className={`relative h-full min-h-0 min-w-0 overflow-auto overscroll-y-contain ${hasMobileNavigation ? '' : 'pb-[env(safe-area-inset-bottom)]'}`}
              data-testid="appContent_routeHost"
            >
              <Suspense fallback={<FallbackSpinner />}>
                <Routes>{renderRoutes()}</Routes>
              </Suspense>
            </div>
          )}
        </main>
        <GlobalChrome
          userRole={userRole}
          shouldHideFloatingChat={shouldHideFloatingSupportActions}
        />
        <ViewSettingsSheet
          open={showViewSettings}
          onOpenChange={setShowViewSettings}
          viewSettings={viewSettings}
          updateViewSetting={updateViewSetting}
          updateClientDetailsTabSetting={updateClientDetailsTabSetting}
          updateClientDiscoveryTabSetting={updateClientDiscoveryTabSetting}
          updateCorporateDashboardTabSetting={updateCorporateDashboardTabSetting}
          resetViewSettings={resetViewSettings}
          flags={flags}
        />
      </div>
    </div>
  );
};
